import { useEffect, useMemo, useState, type RefObject } from "react";
import type { CollabUser } from "@ease/page-builder/collaboration";
import { Circle } from "lucide-react";

interface CollabCursorPosition {
  userId: string;
  displayName: string;
  color: string;
  left: number;
  top: number;
}

interface CollabCursorsProps {
  canvasRef: RefObject<HTMLElement | null>;
  collaborators: CollabUser[];
  currentUserId?: string;
  observedVersion: unknown;
}

export function CollabCursors({
  canvasRef,
  collaborators,
  currentUserId,
  observedVersion,
}: CollabCursorsProps) {
  const [positions, setPositions] = useState<CollabCursorPosition[]>([]);

  const remoteCollaborators = useMemo(
    () => collaborators.filter((entry) => entry.userId !== currentUserId && entry.cursor),
    [collaborators, currentUserId],
  );

  useEffect(() => {
    const updatePositions = () => {
      const canvas = canvasRef.current;
      if (!canvas) {
        setPositions([]);
        return;
      }

      const canvasRect = canvas.getBoundingClientRect();
      const nextPositions = remoteCollaborators.flatMap((collaborator) => {
        const cursor = collaborator.cursor;
        if (!cursor) {
          return [];
        }

        const node = Array.from(canvas.querySelectorAll<HTMLElement>("[data-block-id]"))
          .find((entry) => entry.dataset.blockId === cursor.componentId);
        if (!node) {
          return [];
        }

        const nodeRect = node.getBoundingClientRect();
        return [{
          userId: collaborator.userId,
          displayName: collaborator.displayName,
          color: collaborator.color,
          left: Math.max(8, nodeRect.left - canvasRect.left + 8),
          top: Math.max(8, nodeRect.top - canvasRect.top + nodeRect.height * cursor.relativeY),
        }];
      });

      setPositions(nextPositions);
    };

    updatePositions();
    window.addEventListener("resize", updatePositions);
    return () => window.removeEventListener("resize", updatePositions);
  }, [canvasRef, observedVersion, remoteCollaborators]);

  if (positions.length === 0) {
    return null;
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-30">
      {positions.map((position) => (
        <div
          key={position.userId}
          className="absolute"
          style={{ left: position.left, top: position.top }}
        >
          <div className="pointer-events-none absolute -top-6 left-0 flex items-center gap-1">
            <Circle className="h-2 w-2 fill-current" style={{ color: position.color }} />
            <span className="rounded px-1.5 py-0.5 text-[10px] font-medium text-white" style={{ backgroundColor: position.color }}>
              {position.displayName}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
